import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import Select from "react-select";
import toast from "react-hot-toast";
import { isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  LockIconGrey,
  LockIconSizeOnButton,
  LockIconSizeTable,
  LockIconWhiteOnButton,
} from "../utils/LockIconforPermission";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import Pagination from "./Pagination";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";
import { hrmsSuccessMessage } from "../utils/hrmsSuccessMessage";
import { formatInr } from "../utils/hrmsMoney";
import { formatDateToDDMMYYYY } from "../pages/const";
import { HrmsBranchSelect } from "./HrmsBranchSelect";
import { useHrmsEmployees } from "../hooks/useHrmsEmployees";

const PROFILE_LOCK_ICON = "solar:lock-bold";

const PROFILE_NO_ACCESS_MSG =
  "You do not have permission to view or edit payroll profiles.";

const PROFILE_VIEW_ONLY_MSG =
  "You don't have permission to view the payroll profile.";

function ProfileNoAccessMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={PROFILE_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{PROFILE_NO_ACCESS_MSG}</span>
    </span>
  );
}

function ProfileViewLockedMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={PROFILE_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{PROFILE_VIEW_ONLY_MSG}</span>
    </span>
  );
}

function ProfileEditLockedHint({ title }) {
  return (
    <span
      className="d-inline-flex align-items-center"
      title={
        title || "No permission to edit payroll profiles (HRMS_PROFILE_EDIT)."
      }
      role="img"
      aria-label="No permission to edit payroll profiles"
    >
      <Icon
        icon={PROFILE_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
    </span>
  );
}

function ProfileLockOnButton({ onSolidButton = false }) {
  return (
    <Icon
      icon={PROFILE_LOCK_ICON}
      className="me-1"
      width={LockIconSizeOnButton}
      height={LockIconSizeOnButton}
      style={{
        color: onSolidButton ? LockIconWhiteOnButton : LockIconGrey,
      }}
      aria-hidden
    />
  );
}

function extractProfiles(res) {
  const d = res?.data;
  if (Array.isArray(d?.profiles)) return d.profiles;
  if (Array.isArray(d?.data)) return d.data;
  return [];
}

function parseNum(raw) {
  const s = String(raw ?? "")
    .replace(/,/g, "")
    .trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function ProfilesTab({
  branchId,
  onBranchChange,
  branchOptions,
  branchesLoading,
}) {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.HRMS_PROFILE_VIEW);
  const canEdit = hasPermission(PERMISSIONS.HRMS_PROFILE_EDIT);
  const hasAnyAccess = canView || canEdit;
  const editLockedTitle =
    "No permission to edit payroll profiles (HRMS_PROFILE_EDIT).";

  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);

  const [formBranchId, setFormBranchId] = useState(null);
  const { selectOptions: empOptions, loading: empLoading } =
    useHrmsEmployees(formBranchId);

  const [showModal, setShowModal] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeUa, setActiveUa] = useState(null);
  const [formEmp, setFormEmp] = useState(null);
  const [formSalary, setFormSalary] = useState("");
  const [formOt, setFormOt] = useState("");
  const [formJoin, setFormJoin] = useState("");
  const [formBank, setFormBank] = useState("");
  const [formIfsc, setFormIfsc] = useState("");
  const [formPan, setFormPan] = useState("");
  const [empName, setEmpName] = useState("");
  const [empPhone, setEmpPhone] = useState("");

  const selectStyles = useMemo(
    () => ({
      control: (p) => ({
        ...p,
        minHeight: 44,
        minWidth: 200,
        borderRadius: buttonTableStyle === "square" ? 0 : 8,
        borderColor: "#e0e6eb",
        boxShadow: "none",
      }),
      menu: (p) => ({ ...p, zIndex: 9999 }),
    }),
    [buttonTableStyle],
  );

  useEffect(() => {
    setRows([]);
  }, [branchId]);

  const fetchList = useCallback(async () => {
    if (!branchId || !hasAnyAccess || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await hrmsApi("profile_list", {
        branch_id: Number(branchId),
      });
      if (isErpApiSuccess(res) && res.data) {
        setRows(extractProfiles(res));
      } else {
        setRows([]);
        if (res?.success === false && res?.message) toast.error(res.message);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load payroll profiles.");
    } finally {
      setLoading(false);
    }
  }, [branchId, canView, hasAnyAccess]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [branchId, searchQuery]);

  const applyProfileFields = useCallback((p) => {
    setFormSalary(
      p.base_monthly_salary != null ? String(p.base_monthly_salary) : "",
    );
    setFormOt(p.ot_rate_per_hour != null ? String(p.ot_rate_per_hour) : "");
    setFormJoin(p.join_date ? String(p.join_date).split("T")[0] : "");
    setFormBank(String(p.bank_account_no ?? ""));
    setFormIfsc(String(p.bank_ifsc ?? ""));
    setFormPan(String(p.pan_number ?? ""));
    setEmpName(String(p.user_name || "").trim());
    setEmpPhone(String(p.user_phone || "").trim());
  }, []);

  const onFormBranchChange = (id) => {
    setFormBranchId(id);
    setFormEmp(null);
    setActiveUa(null);
    setEmpName("");
    setEmpPhone("");
  };

  const resetFormFields = () => {
    setFormEmp(null);
    setActiveUa(null);
    setFormSalary("");
    setFormOt("");
    setFormJoin("");
    setFormBank("");
    setFormIfsc("");
    setFormPan("");
    setEmpName("");
    setEmpPhone("");
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setIsAddMode(false);
    setFormBranchId(null);
    resetFormFields();
  };

  const openAdd = () => {
    if (!canEdit) return;
    setIsAddMode(true);
    resetFormFields();
    setFormBranchId(branchId ?? null);
    setShowModal(true);
  };

  const openEdit = async (r) => {
    if (!canEdit) return;
    const ua = Number(r.user_admin_id);
    if (!Number.isFinite(ua) || ua <= 0) return;
    setIsAddMode(false);
    const rowBranch = Number(r.branch_id ?? branchId);
    setFormBranchId(
      Number.isFinite(rowBranch) && rowBranch > 0 ? rowBranch : branchId,
    );
    setActiveUa(ua);
    setFormEmp({
      value: ua,
      label: String(r.user_name || "").trim() || `User #${ua}`,
    });
    applyProfileFields(r);
    setShowModal(true);
    const showBranch =
      Number.isFinite(rowBranch) && rowBranch > 0
        ? rowBranch
        : Number(branchId);
    if (!canView || !showBranch) return;
    try {
      const res = await hrmsApi("profile_show", {
        branch_id: showBranch,
        user_admin_id: ua,
      });
      if (isErpApiSuccess(res) && res.data?.profile) {
        applyProfileFields(res.data.profile);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load profile detail.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      toast.error("You do not have permission to edit profiles.");
      return;
    }
    if (!formBranchId) {
      toast.error("Select a branch.");
      return;
    }

    const ua = isAddMode ? Number(formEmp?.value) : Number(activeUa);
    if (!Number.isFinite(ua) || ua <= 0) {
      toast.error("Select an employee.");
      return;
    }

    const baseSalary = parseNum(formSalary);
    if (baseSalary == null || baseSalary < 0) {
      toast.error("Enter a valid base monthly salary.");
      return;
    }

    const body = {
      branch_id: Number(formBranchId),
      user_admin_id: ua,
      base_monthly_salary: baseSalary,
    };

    const ot = parseNum(formOt);
    if (ot != null) body.ot_rate_per_hour = ot;

    const join = String(formJoin || "").trim();
    if (join) body.join_date = join;

    const bank = String(formBank || "").trim();
    if (bank) body.bank_account_no = bank;

    const ifsc = String(formIfsc || "")
      .trim()
      .toUpperCase();
    if (ifsc) body.bank_ifsc = ifsc;

    const pan = String(formPan || "")
      .trim()
      .toUpperCase();
    if (pan) body.pan_number = pan;

    setSaving(true);
    try {
      const res = await hrmsApi("profile_save", body);
      if (isErpApiSuccess(res)) {
        const label =
          String(res.data?.profile?.user_name || empName || "").trim() ||
          `User #${ua}`;
        toast.success(
          hrmsSuccessMessage(
            res,
            isAddMode
              ? `Payroll profile added for ${label}.`
              : `Payroll profile updated for ${label}.`,
          ),
        );
        closeModal();
        await fetchList();
      } else {
        toast.error(res?.message || "Could not save profile.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) =>
        String(a.user_name || "").localeCompare(String(b.user_name || "")),
      ),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((r) => {
      const text = [
        r.user_name,
        r.user_phone,
        r.bank_account_no,
        r.bank_ifsc,
        r.pan_number,
        r.join_date ? formatDateToDDMMYYYY(r.join_date) : "",
        String(r.base_monthly_salary ?? ""),
        String(r.ot_rate_per_hour ?? ""),
        formatInr(r.base_monthly_salary),
        formatInr(r.ot_rate_per_hour),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(q);
    });
  }, [sorted, searchQuery]);

  const displayRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const showActionsColumn = hasAnyAccess;
  const colCount = showActionsColumn ? 8 : 7;

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
          <div className="d-flex align-items-center gap-3 flex-nowrap">
            <HrmsBranchSelect
              branchId={branchId}
              onChangeBranchId={onBranchChange}
              branchOptions={branchOptions}
              loading={branchesLoading}
              buttonTableStyle={buttonTableStyle}
              disabled={!hasAnyAccess}
            />
            <input
              type="text"
              className={`form-control flex-shrink-0 ${buttonClass}`}
              style={{ width: 260 }}
              placeholder="Search..."
              value={searchQuery}
              onChange={(ev) => {
                setSearchQuery(ev.target.value);
                setCurrentPage(1);
              }}
              disabled={!hasAnyAccess || !canView}
              aria-label="Search payroll profiles"
            />
          </div>
          {hasAnyAccess ? (
            <Button
              type="button"
              className={`btn btn-success flex-shrink-0 ${buttonClass}`}
              disabled={!canEdit}
              title={!canEdit ? editLockedTitle : undefined}
              onClick={canEdit ? openAdd : undefined}
            >
              {!canEdit ? <ProfileLockOnButton onSolidButton /> : null}
              Add payroll profile
            </Button>
          ) : null}
        </div>
      </div>

      <div className="col-12">
        <div
          className={`table-responsive border position-relative ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
        >
          {!hasAnyAccess ? (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-90"
              style={{ zIndex: 5 }}
            >
              <ProfileNoAccessMessage />
            </div>
          ) : null}
          <table
            className={`table text-nowrap mb-0 align-middle ${tableClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
          >
            <thead className="text-dark fs-4">
              <tr>
                <th>Employee</th>
                <th className="text-end">Monthly Salary</th>
                <th className="text-end">OT rate / hr</th>
                <th>Join date</th>
                <th>Bank account</th>
                <th>IFSC</th>
                <th>PAN</th>
                {showActionsColumn ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {!hasAnyAccess ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    <ProfileNoAccessMessage />
                  </td>
                </tr>
              ) : !canView ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    <ProfileViewLockedMessage />
                  </td>
                </tr>
              ) : !branchId ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    Select a branch to view payroll profiles.
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    Loading…
                  </td>
                </tr>
              ) : displayRows.length > 0 ? (
                displayRows.map((r) => {
                  const rowKey = r.id ?? r.user_admin_id;
                  const ua = Number(r.user_admin_id);
                  return (
                    <tr key={rowKey}>
                      <td>
                        {String(r.user_name || "").trim() || `User #${ua}`}
                      </td>
                      <td className="text-end">
                        {formatInr(r.base_monthly_salary)}
                      </td>
                      <td className="text-end">
                        {formatInr(r.ot_rate_per_hour)}
                      </td>
                      <td>
                        {r.join_date ? formatDateToDDMMYYYY(r.join_date) : "—"}
                      </td>
                      <td className="text-wrap" style={{ maxWidth: "12rem" }}>
                        {String(r.bank_account_no || "—")}
                      </td>
                      <td>{String(r.bank_ifsc || "—")}</td>
                      <td>{String(r.pan_number || "—")}</td>
                      {showActionsColumn ? (
                        <td>
                          {canEdit ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              className={buttonClass}
                              onClick={() => void openEdit(r)}
                            >
                              <Icon icon="solar:pen-bold" width={16} />
                            </Button>
                          ) : (
                            <ProfileEditLockedHint title={editLockedTitle} />
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    {sorted.length > 0
                      ? "No profiles match your search."
                      : "No payroll profiles for this branch. Add one to get started."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {hasAnyAccess && canView && filteredRows.length > 0 ? (
        <div className="col-12">
          <Pagination
            totalItems={filteredRows.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      ) : null}

      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            {isAddMode
              ? "Add payroll profile"
              : `Edit payroll profile${empName ? ` — ${empName}` : ""}`}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={submit}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <Form.Label>Branch <span className="text-danger">*</span></Form.Label>
                <HrmsBranchSelect
                  branchId={formBranchId}
                  onChangeBranchId={onFormBranchChange}
                  branchOptions={branchOptions}
                  loading={branchesLoading}
                  buttonTableStyle={buttonTableStyle}
                  disabled={saving || !isAddMode}
                />
              </div>
              <div className="col-12">
                <Form.Label>
                  Employee <span className="text-danger">*</span>
                </Form.Label>
                {isAddMode ? (
                  <Select
                    inputId="hrms-profile-emp"
                    options={empOptions}
                    value={formEmp}
                    onChange={(opt) => {
                      setFormEmp(opt);
                      setActiveUa(opt ? Number(opt.value) : null);
                      setEmpName(String(opt?.label || ""));
                      const phone = String(opt?.raw?.phone || "").trim();
                      setEmpPhone(phone);
                    }}
                    isLoading={empLoading}
                    isDisabled={saving || !formBranchId}
                    placeholder={
                      formBranchId ? "Select employee" : "Select branch first"
                    }
                    styles={selectStyles}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    value={
                      empName
                        ? `${empName}${empPhone ? ` (${empPhone})` : ""}`
                        : ""
                    }
                    disabled
                    readOnly
                  />
                )}
              </div>
              <div className="col-md-6">
                <Form.Label>
                  Base monthly salary (₹) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={formSalary}
                  onChange={(ev) => setFormSalary(ev.target.value)}
                  disabled={!canEdit || saving}
                  required
                  placeholder="e.g. 25000"
                />
              </div>
              <div className="col-md-6">
                <Form.Label>OT rate / hour (₹)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={formOt}
                  onChange={(ev) => setFormOt(ev.target.value)}
                  disabled={!canEdit || saving}
                  placeholder="e.g. 150"
                />
              </div>
              <div className="col-md-6">
                <Form.Label>Join date</Form.Label>
                <Form.Control
                  type="date"
                  value={formJoin}
                  onChange={(ev) => setFormJoin(ev.target.value)}
                  disabled={!canEdit || saving}
                />
              </div>
              <div className="col-md-6">
                <Form.Label>Bank account no.</Form.Label>
                <Form.Control
                  value={formBank}
                  onChange={(ev) => setFormBank(ev.target.value)}
                  disabled={!canEdit || saving}
                  placeholder="e.g. 1234567890"
                />
              </div>
              <div className="col-md-6">
                <Form.Label>Bank IFSC</Form.Label>
                <Form.Control
                  value={formIfsc}
                  onChange={(ev) => setFormIfsc(ev.target.value)}
                  disabled={!canEdit || saving}
                  placeholder="e.g. SBIN0001234"
                />
              </div>
              <div className="col-md-6">
                <Form.Label>PAN</Form.Label>
                <Form.Control
                  value={formPan}
                  onChange={(ev) => setFormPan(ev.target.value)}
                  disabled={!canEdit || saving}
                  placeholder="e.g. ABCDE1234F"
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            {canEdit ? (
              <Button
                type="submit"
                variant="success"
                className={buttonClass}
                disabled={saving}
              >
                {saving ? "Saving…" : isAddMode ? "Add profile" : "Save"}
              </Button>
            ) : null}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
